import { type MenuInputOption } from '@saviynt/design-system';

import type {
  BooleanField,
  CaptchaField,
  CheckboxField,
  DataProvider,
  DatastoreProvider,
  DataTableField,
  DateField,
  DateTimeField,
  Field,
  FieldElement,
  MultiSelectField,
  NumberField,
  PasswordField,
  RadioField,
  RichTextField,
  Row,
  Section,
  SensitiveField,
  SingleSelectField,
  SpacerField,
  TermsAndConditionsField,
  TextareaField,
  TextField,
} from '../types/types';

// TODO: This files checks seems to be missing some fields, needs to be checked and updated

// Type guards for runtime type checking
export const isSection = (element: FieldElement): element is Section =>
  element.type === 'section';

export const isRow = (element: FieldElement): element is Row =>
  element.type === 'row';

export const isField = (element: FieldElement): element is Field =>
  element.type === 'field';

export const isTextField = (element: Field): element is TextField =>
  element.fieldType === 'text';

export const isTextareaField = (element: Field): element is TextareaField =>
  element.fieldType === 'textarea';

export const isCheckboxField = (element: Field): element is CheckboxField =>
  element.fieldType === 'checkbox';

export const isRadioField = (element: Field): element is RadioField =>
  element.fieldType === 'radio';

export const isSingleSelectField = (
  element: Field
): element is SingleSelectField => element.fieldType === 'singleselect';

export const isMultiSelectField = (
  element: Field
): element is MultiSelectField => element.fieldType === 'multiselect';

export const isDataTableField = (element: Field): element is DataTableField =>
  element.fieldType === 'datatable';

export const isDateField = (element: Field): element is DateField =>
  element.fieldType === 'date';

export const isDateTimeField = (element: Field): element is DateTimeField =>
  element.fieldType === 'datetime';

export const isSpacerField = (element: Field): element is SpacerField =>
  element.fieldType === 'spacer';

export const isNumberField = (element: Field): element is NumberField =>
  element.fieldType === 'number';

export const isBooleanField = (element: Field): element is BooleanField =>
  element.fieldType === 'boolean';

export const isRichTextField = (element: Field): element is RichTextField =>
  element.fieldType === 'richtext';

export const isTermsAndConditionsField = (
  element: Field
): element is TermsAndConditionsField => element.fieldType === 'tnc';

export const isSensitiveField = (element: Field): element is SensitiveField =>
  element.fieldType === 'sensitive';

export const isPasswordField = (element: Field): element is PasswordField =>
  element.fieldType === 'password';

export const isCaptchaField = (element: Field): element is CaptchaField =>
  element.fieldType === 'captcha';

export const isDatastoreProvider = (
  dp: DataProvider
): dp is DatastoreProvider => dp.type === 'DATASTORE';

const isMenuInputOption = (v: unknown): v is MenuInputOption =>
  typeof v === 'object' && v !== null && 'value' in v && 'label' in v;

export const isMenuInputOptionArray = (v: unknown): v is MenuInputOption[] =>
  Array.isArray(v) && v.every(isMenuInputOption);

export const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

// Helper to check if field has a property
export const hasPlaceholder = (
  field: Field
): field is
  | TextField
  | NumberField
  | TextareaField
  | DateField
  | SingleSelectField
  | MultiSelectField
  | PasswordField => 'placeholder' in field;
export const hasHelperText = (
  field: Field
): field is
  | TextField
  | NumberField
  | TextareaField
  | DateField
  | SingleSelectField
  | MultiSelectField
  | RadioField
  | CheckboxField
  | BooleanField
  | PasswordField
  | DataTableField => 'helperText' in field;
export const hasColSpan = (
  field: Field
): field is
  | TextField
  | NumberField
  | TextareaField
  | DateField
  | SingleSelectField
  | MultiSelectField
  | RadioField
  | CheckboxField
  | BooleanField
  | PasswordField
  | DataTableField
  | RichTextField
  | TermsAndConditionsField
  | CaptchaField => 'colSpan' in field;
export const hasIsHidden = (
  field: Field
): field is
  | TextField
  | NumberField
  | TextareaField
  | DateField
  | SingleSelectField
  | MultiSelectField
  | RadioField
  | CheckboxField
  | BooleanField
  | PasswordField
  | DataTableField
  | RichTextField
  | TermsAndConditionsField => 'isHidden' in field;
export const hasIsReadOnly = (
  field: Field
): field is
  | TextField
  | NumberField
  | TextareaField
  | DateField
  | SingleSelectField
  | MultiSelectField
  | BooleanField
  | PasswordField
  | DataTableField => 'isReadOnly' in field;
export const hasDefaultValue = (
  field: Field
): field is
  | TextField
  | NumberField
  | TextareaField
  | DateField
  | BooleanField => 'defaultValue' in field;
export const hasValidations = (
  field: Field
): field is TextField | NumberField | TextareaField | PasswordField =>
  'validations' in field;
export const hasInfoTooltip = (
  field: Field
): field is
  | TextField
  | NumberField
  | TextareaField
  | DateField
  | SingleSelectField
  | MultiSelectField
  | RadioField
  | CheckboxField
  | BooleanField
  | PasswordField
  | DataTableField
  | TermsAndConditionsField => 'infoTooltipText' in field;
export const hasAdvConfig = (
  field: Field
): field is PasswordField | DateField => 'advConfig' in field;
export const hasPasswordConfirmation = (field: Field): field is PasswordField =>
  hasAdvConfig(field) &&
  field.advConfig !== null &&
  field.advConfig !== undefined &&
  'isPasswordConfirmation' in field.advConfig;
export const hasAutoGeneration = (field: Field): field is PasswordField =>
  hasAdvConfig(field) &&
  field.advConfig !== null &&
  field.advConfig !== undefined &&
  'isAutoGenerationEnabled' in field.advConfig;
